import React, { useEffect, useState, useCallback } from 'react';
import { FeedPost, JournalEntry, PublicProfile, SessionUser } from '../types';
import { MomentCard } from '../components/MomentCard';
import { EmptyState } from '../components/EmptyState';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

interface Props {
  username?: string;
  onNavigate: (page: string, params?: Record<string, string>) => void;
  currentUser: SessionUser | null;
  onLoginRequired?: () => void;
}

export function ProfilePage({ username, onNavigate, currentUser, onLoginRequired }: Props) {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [journal, setJournal] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [postsInitialized, setPostsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // No username prop = viewing own profile; or username matches current user's username
  // Fall back to id-based check when username isn't set yet
  const isOwnProfile = !username || (!!currentUser && (
    username === currentUser.username || (!currentUser.username && !username)
  ));

  useEffect(() => {
    setProfile(null);
    setPosts([]);
    setNextCursor(null);
    setPostsInitialized(false);
    setError(null);

    async function load() {
      try {
        if (isOwnProfile) {
          const [meRes, journalRes] = await Promise.all([
            fetch('/api/v1/users/me'),
            fetch('/api/v1/users/me/journal'),
          ]);
          if (!meRes.ok) { setError('Failed to load profile'); return; }
          const meJson = await meRes.json() as { data: PublicProfile };
          const journalJson = await journalRes.json() as { data: JournalEntry[] };
          setProfile(meJson.data);
          setJournal(journalJson.data);
        } else {
          const res = await fetch(`/api/v1/users/${username}`);
          if (res.status === 404) { setError('User not found'); return; }
          const json = await res.json() as { data: PublicProfile };
          setProfile(json.data);
          setFollowing(json.data.is_following);
        }
      } catch {
        setError('Failed to load profile');
      }
    }
    load();
  }, [username]);

  // Update page title when profile loads
  useEffect(() => {
    if (!profile) return;
    const name = profile.display_name ?? profile.username ?? 'Profile';
    document.title = `${name} — mylodic`;
    return () => { document.title = 'mylodic'; };
  }, [profile]);

  const loadPosts = useCallback(async (cursor: string | null) => {
    if (loading || !profile) return;
    // Own profile uses /me/posts (works even without a username set)
    // Other profiles require a username
    const base = isOwnProfile
      ? '/api/v1/users/me/posts'
      : profile.username ? `/api/v1/users/${profile.username}/posts` : null;
    if (!base) { setPostsInitialized(true); return; }
    setLoading(true);
    try {
      const url = cursor ? `${base}?cursor=${encodeURIComponent(cursor)}` : base;
      const res = await fetch(url);
      if (!res.ok) { setPostsInitialized(true); return; }
      const json = await res.json() as { data: FeedPost[]; next_cursor: string | null };
      setPosts(prev => cursor ? [...prev, ...json.data] : json.data);
      setNextCursor(json.next_cursor);
      setPostsInitialized(true);
    } catch {
      setPostsInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [loading, profile, isOwnProfile]);

  useEffect(() => {
    if (profile && !postsInitialized) loadPosts(null);
  }, [profile]); // eslint-disable-line

  const handleLoadMore = useCallback(() => loadPosts(nextCursor), [nextCursor, loadPosts]);
  const sentinelRef = useInfiniteScroll(handleLoadMore, !!nextCursor && !loading);

  async function toggleFollow() {
    if (!profile || !currentUser || followLoading) return;
    const wasFollowing = following;
    setFollowLoading(true);
    setFollowing(!wasFollowing);
    setProfile(p => p ? { ...p, follower_count: p.follower_count + (wasFollowing ? -1 : 1) } : p);
    try {
      const res = await fetch(`/api/v1/users/${profile.id}/follow`, { method: wasFollowing ? 'DELETE' : 'POST' });
      if (!res.ok) throw new Error();
    } catch {
      setFollowing(wasFollowing);
      setProfile(p => p ? { ...p, follower_count: p.follower_count + (wasFollowing ? 1 : -1) } : p);
    } finally {
      setFollowLoading(false);
    }
  }

  if (error) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%', color: 'var(--text-muted)' }}>{error}</div>
  );

  if (!profile) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%', color: 'var(--text-muted)' }}>Loading...</div>
  );

  const last28 = journal.slice(-28);

  return (
    <div className="fade-in">
      {/* Sticky header */}
      <div style={stickyHeader}>
        {!isOwnProfile && (
          <button onClick={() => onNavigate('feed')} style={backBtn}>
            <ChevronLeft />
          </button>
        )}
        <span style={{ fontWeight: 700, fontSize: 16 }}>
          {profile.display_name ?? profile.username ?? 'Profile'}
        </span>
        <div style={{ width: 28 }} />
      </div>

      {/* Hero */}
      <div style={heroSection}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <AvatarLarge url={profile.avatar_url} name={profile.display_name ?? profile.username ?? '?'} />
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {isOwnProfile ? (
              <button onClick={() => onNavigate('edit-profile')} style={outlineBtnStyle}>
                Edit profile
              </button>
            ) : currentUser ? (
              <button onClick={toggleFollow} disabled={followLoading} style={following ? outlineBtnStyle : fillBtnStyle}>
                {following ? 'Following' : 'Follow'}
              </button>
            ) : null}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{profile.display_name ?? profile.username}</div>
          {profile.username && <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 1 }}>@{profile.username}</div>}
          {profile.bio && <p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6, color: 'var(--text)' }}>{profile.bio}</p>}
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 20, marginTop: 14 }}>
          <Stat value={profile.post_count} label="posts" />
          <Stat value={profile.follower_count} label="followers" />
          <Stat value={profile.following_count} label="following" />
        </div>
      </div>

      {/* Sound journal */}
      {isOwnProfile && last28.length > 0 && (
        <div style={journalSection}>
          <div style={sectionLabel}>Sound journal</div>
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {last28.map(entry => (
              <div
                key={entry.date}
                title={entry.date}
                style={{
                  width: 10, height: 10,
                  borderRadius: 2,
                  background: entry.content_type ? 'var(--accent)' : 'var(--border)',
                  opacity: entry.content_type ? 0.85 : 0.25,
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)' }} />

      {/* Posts */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {posts.map(post => (
          <div key={post.id} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px' }}>
            <MomentCard post={post} currentUserId={currentUser?.id} onLoginRequired={onLoginRequired} onResonate={() => {}} onReply={p => onNavigate('post-detail', { postId: p.id })} onOpen={p => onNavigate('post-detail', { postId: p.id })} onNavigateUser={u => onNavigate('user', { username: u })} onDelete={id => setPosts(prev => prev.filter(p => p.id !== id))} onUpdate={updated => setPosts(prev => prev.map(p => p.id === updated.id ? updated : p))} />
          </div>
        ))}

        {loading && <Spinner />}

        {postsInitialized && posts.length === 0 && !loading && (
          <EmptyState heading="No moments yet" body="Posts shared here will appear in this space." />
        )}

        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <span style={{ fontWeight: 700, fontSize: 16 }}>{value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}</span>
      <span style={{ color: 'var(--text-muted)', fontSize: 14, marginLeft: 4 }}>{label}</span>
    </div>
  );
}

function AvatarLarge({ url, name }: { url: string | null; name: string }) {
  const [failed, setFailed] = React.useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name}
        style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--surface2)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700 }}>
      {(name?.[0] ?? '?').toUpperCase()}
    </div>
  );
}

function ChevronLeft() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: 24 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

const stickyHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', position: 'sticky', top: 0,
  background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--border)', zIndex: 10,
};

const backBtn: React.CSSProperties = {
  color: 'var(--text-muted)', display: 'flex', alignItems: 'center', width: 28,
};

const heroSection: React.CSSProperties = {
  padding: '20px 16px 16px',
};

const journalSection: React.CSSProperties = {
  padding: '10px 16px',
  borderTop: '1px solid var(--border)',
};

const sectionLabel: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10,
};

const outlineBtnStyle: React.CSSProperties = {
  padding: '7px 16px', borderRadius: 100, border: '1px solid var(--border)',
  fontSize: 13, fontWeight: 600, color: 'var(--text)',
};

const fillBtnStyle: React.CSSProperties = {
  padding: '7px 16px', borderRadius: 100, background: 'var(--accent)',
  fontSize: 13, fontWeight: 600, color: '#fff', border: 'none',
};
